import React from "react"
import PropTypes from "prop-types"
import cn from "classnames"
import { propsUtil } from "@asphalt-react/helper"

import {
  isBoldSize,
  resolveBoldSizeProp,
  resolveTextSizeProp,
  resolveIntent,
} from "./props.js"
import styles from "./text.module.css"
import intentStyles from "./intent.module.css"

const { concatClasses, resolvePropCollision } = propsUtil

export const Text = ({
  children,
  size = "m",
  bold,
  p = false,
  span = false,
  div = false,
  primary = false,
  secondary = false,
  brand = false,
  onBrand = false,
  danger = false,
  warning = false,
  info = false,
  success = false,
  muted = false,
  ellipsis = false,
  ...props
}) => {
  /* eslint-disable-next-line react/prop-types */
  const { style, className, ...rest } = props

  let isBold = bold

  const { collision, value: Tag } = resolvePropCollision(
    {
      p,
      span,
      div,
    },
    "p"
  )

  if (collision) {
    console.warn(`Text: Prop collision detected, falling back to "${Tag}".`)
  }

  if (bold && !isBoldSize(size)) {
    console.warn(
      'Text: Bold variant is only supported for "xs", "s", "m" & "l" sizes.'
    )
    isBold = false
  }

  const { collision: intentCollision, value: intent } = resolveIntent({
    primary,
    secondary,
    brand,
    onBrand,
    danger,
    warning,
    info,
    success,
    muted,
  })

  if (intentCollision) {
    console.warn(
      `Text: Multiple intents detected, falling back to "${intent}" intent.`
    )
  }

  const classes = cn(
    styles.Text,
    {
      [styles[`${concatClasses("text", resolveTextSizeProp(size))}`]]: !isBold,
    },
    { [styles[`${concatClasses("bold", resolveBoldSizeProp(size))}`]]: isBold },
    { [styles.ellipsis]: ellipsis },
    intentStyles[intent]
  )

  return (
    <Tag {...rest} className={classes}>
      {children}
    </Tag>
  )
}

Text.displayName = "Text"

Text.propTypes = {
  /**
   * React node for text content.
   */
  children: PropTypes.node.isRequired,
  /**
   * Controls the size of text.
   */
  size: PropTypes.oneOf(["xs", "s", "m", "l", "xl", "xxl"]),
  /**
   * Render the bold variant.
   *
   * Bold variant is only supported for "xs", "s", "m" & "l" sizes.
   */
  bold: PropTypes.bool,
  /**
   * render the content in a `p` tag.
   */
  p: PropTypes.bool,
  /**
   * render the content in a `span` tag.
   */
  span: PropTypes.bool,
  /**
   * render the content in a `div` tag.
   */
  div: PropTypes.bool,
  /**
   * Renders the text with primary intent. This is the default intent.
   */
  primary: PropTypes.bool,
  /**
   * Renders the text with secondary intent.
   */
  secondary: PropTypes.bool,
  /**
   * Renders the text with brand intent.
   */
  brand: PropTypes.bool,
  /**
   * Renders the text with appropriate contrast ratio to backgrounds with brand color.
   */
  onBrand: PropTypes.bool,
  /**
   * Renders the text with danger intent.
   */
  danger: PropTypes.bool,
  /**
   * Renders the text with warning intent.
   */
  warning: PropTypes.bool,
  /**
   * Renders the text with info intent.
   */
  info: PropTypes.bool,
  /**
   * Renders the text with success intent.
   */
  success: PropTypes.bool,
  /**
   * Renders the text with muted intent.
   */
  muted: PropTypes.bool,
  /**
   * Truncates text with an ellipsis when it overflows its container.
   */
  ellipsis: PropTypes.bool,
}

Text.defaultProps = {
  size: "m",
  p: false,
  span: false,
  div: false,
  primary: false,
  secondary: false,
  brand: false,
  onBrand: false,
  danger: false,
  warning: false,
  info: false,
  success: false,
  ellipsis: false,
  muted: false,
}
